/**
 * ProcessingBadge
 * Visual indicator for requests in PROCESSING stage
 * Shows animated badge with pulsing effect
 */

import { Loader } from 'lucide-react-native';
import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, Platform } from 'react-native';

export interface ProcessingBadgeProps {
  stage: string;
  size?: 'small' | 'medium' | 'large';
}

export const ProcessingBadge: React.FC<ProcessingBadgeProps> = ({
  stage,
  size = 'medium',
}) => {
  const spinValue = useRef(new Animated.Value(0)).current;
  const pulseValue = useRef(new Animated.Value(1)).current;
  const isProcessing = stage === 'PROCESSING';

  // Spin animation for loader icon (runs only when PROCESSING)
  useEffect(() => {
    if (!isProcessing) {
      return;
    }

    const animation = Animated.loop(
      Animated.timing(spinValue, {
        toValue: 1,
        duration: 2000,
        useNativeDriver: Platform.OS !== 'web',
      }),
    );
    animation.start();

    return () => animation.stop();
  }, [spinValue, isProcessing]);

  // Pulse animation for badge (runs only when PROCESSING)
  useEffect(() => {
    if (!isProcessing) {
      return;
    }

    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseValue, {
          toValue: 1.05,
          duration: 1000,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(pulseValue, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]),
    );
    animation.start();

    return () => animation.stop();
  }, [pulseValue, isProcessing]);

  // Only render for PROCESSING stage
  if (!isProcessing) {
    return null;
  }

  const spin = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  const sizeStyles = {
    small: styles.smallBadge,
    medium: styles.mediumBadge,
    large: styles.largeBadge,
  };

  const textStyles = {
    small: styles.smallText,
    medium: styles.mediumText,
    large: styles.largeText,
  };

  const iconSizes = {
    small: 12,
    medium: 14,
    large: 16,
  };

  return (
    <Animated.View
      style={[
        styles.badge,
        sizeStyles[size],
        {
          transform: [{ scale: pulseValue }],
        },
      ]}
    >
      <Animated.View
        style={{
          transform: [{ rotate: spin }],
        }}
      >
        <Loader size={iconSizes[size]} color="#FF9800" style={styles.icon} />
      </Animated.View>
      <Text style={[styles.text, textStyles[size]]}>Processing</Text>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF3E0',
    borderWidth: 1,
    borderColor: '#FFB74D',
    borderRadius: 12,
  },
  smallBadge: {
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  mediumBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  largeBadge: {
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  icon: {
    marginRight: 6,
  },
  text: {
    color: '#FF9800',
    fontWeight: '600',
  },
  smallText: {
    fontSize: 10,
  },
  mediumText: {
    fontSize: 12,
  },
  largeText: {
    fontSize: 14,
  },
});
